import { ApiGetRequest, AppCompany, AppImage } from 'types';

import { ICompanyType } from 'modules/company-type';
import { EEcsWhiteListStatus, IPaymentMethodsSettings } from 'modules/eecs-go';
import { EGrexCountry } from 'modules/grex';

export enum ECompanyRelationType {
  CHILD = 'child',
  PARENT = 'parent',
}

export enum ECompanyRelationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
}

export enum ECompaniesModals {
  CONFIRM = 'confirm',
  TYPE_SELECT = 'type_select',
  RELATIONS_ACTION = 'relations_action',
  RELATIONS_ADD = 'relations_add',
  ONBOARDING_INFO = 'onboarding_info',
}

export enum ECompaniesModalAction {
  DELETE = 'delete',
}

export interface CompaniesModalPayload {
  action?: ECompaniesModalAction;
  selectedRelation?: IRelationCompany;
  selectedRelationType?: ECompanyRelationType;
}

export interface ICompaniesModal {
  type: ECompaniesModals;
  payload?: CompaniesModalPayload;
}

export interface IRelationCompany {
  id: number;
  status: ECompanyRelationStatus;
  initiator_company_id: number;
  company: AppCompany;
}

export interface IOnboardingUser {
  id: number;
  name: string;
  email: string;
}

export interface IOnboardingTrustedCompany {
  id: number;
  name: string;
  status: EEcsWhiteListStatus;
  is_requesting_company: boolean;
}

export interface ICompanyOnboarding {
  is_complete_eecs_flow: boolean;
  necs_account: boolean;
  hknr_account: boolean;
  grex_accounts: Record<EGrexCountry, boolean>;
  incoming_registry: string | null;
  representative_user: IOnboardingUser | null;
  first_signatory_user: IOnboardingUser | null;
  second_signatory_user: IOnboardingUser | null;
  manual: {
    connected: boolean;
    is_active: boolean;
  };
  wallet: {
    connected: boolean;
    is_active: boolean;
  };

  trusted_companies: IOnboardingTrustedCompany[];
}

export interface ICompany {
  id: number;
  uuid: string;
  name: string;
  country_code: string;
  region_code: string;
  active: boolean;
  logo: AppImage | null;
  types: ICompanyType[];
  created_at: string;
  model_type: string;
  onboarding: ICompanyOnboarding | null;
}

export interface ISingleCompany extends ICompany {
  about_us: string | null;
  accounting_email: string;
  as_buyer_fee: string;
  as_seller_fee: string;
  city_code: string;
  email: string;
  facebook: string | null;
  instagram: string | null;
  marketplace_logo: AppImage | null;
  postal_address: string;
  postal_code: string;
  parent_companies: IRelationCompany[];
  child_companies: IRelationCompany[];
  telephone: string;
  terms_and_conditions: string | null;
  telephone_show_in_marketplace: boolean;
  visit_address: string;
  visit_city_code: string;
  visit_postal_code: string;
  website: string | null;
  vat_number: string;
  payment_settings: IPaymentMethodsSettings;
}

export interface GetCompanyPayload {
  id: number;
}

export interface RemoveCompanyPayload extends GetCompanyPayload {}

export interface FetchCompaniesPayload extends ApiGetRequest {
  filter_by_types?: number[] | null;
  filter_by_active?: boolean | null;
  filter_by_created_date_from?: string | null;
  filter_by_created_date_to?: string | null;

  order_by_created_date?: 'asc' | 'desc';
  order_by_geolocation?: 'asc' | 'desc';
}

export interface CreateCompanyPayload {
  name: string;
  vat_number: string;
  postal_address: string;
  postal_code: string;
  country_code: string;
  region_code: string;
  city_code: string;
  email: string;
  accounting_email: string;
  facebook: string;
  instagram: string;
  visit_address: string;
  visit_postal_code: string;
  visit_city_code: string;
  website: string;
  telephone: string;
  about_us: string;
  terms_and_conditions: string;
  telephone_show_in_marketplace: boolean;
  as_seller_fee: string;
  as_buyer_fee: string;
  types: number[];
}

export interface UpdateCompanyPayload extends CreateCompanyPayload {
  id: number;
}

export interface CreateCompanyRelationPayload {
  id: number;
  child_company_id: number;
  parent_company_id: number;
}

export interface UpdateCompanyRelationPayload {
  id: number;
  relation_id: number;
  status: ECompanyRelationStatus;
}

export interface RemoveCompanyRelationPayload {
  id: number;
  relation_id: number;
}
